import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const alertVariants = cva(
  cn(
    'relative w-full rounded-md border p-3.5 text-sm',
    '[&>svg~*]:pl-7 [&>svg+div]:translate-y-[-1px] [&>svg]:absolute [&>svg]:left-3.5 [&>svg]:top-3.5',
  ),
  {
    variants: {
      variant: {
        default:     'border-slate-200 bg-white text-navy-900 [&>svg]:text-navy-600',
        info:        'border-info/30     bg-info-soft     text-info     [&>svg]:text-info',
        positive:    'border-positive/30 bg-positive-soft text-positive [&>svg]:text-positive',
        warning:     'border-warning/30  bg-warning-soft  text-warning  [&>svg]:text-warning',
        destructive: 'border-negative/40 bg-negative-soft text-negative [&>svg]:text-negative',
      },
    },
    defaultVariants: { variant: 'default' },
  },
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
))
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5 ref={ref} className={cn('mb-0.5 text-sm font-semibold leading-tight tracking-tight', className)} {...props} />
  ),
)
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-xs leading-snug opacity-90 [&_p]:leading-relaxed', className)} {...props} />
  ),
)
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription }
